import { ref, watch, computed } from '@vue/composition-api'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import {
  deletePayroll,
  fetchPayrolls as fetchPayrollsSv,
  markAsPaid,
  exportCSVList,
  downloadPayroll,
  sendPayrolls,
} from '@/shared/services/payrollService'
import i18n from '@hw-core/libs/i18n'

export default function usePikapalkkaPaying() {
  // Use toast
  const toast = useToast()

  const refInvoiceListTable = ref(null)
  const formatDate = date => (date ? date.toDate().toLocaleDateString('fi') : '')
  // Table Handlers
  const tableColumns = [
    { key: 'selected' },
    { key: 'index', label: i18n.t('index'), sortable: true },
    { key: 'date', label: i18n.t('date'), sortable: true },
    { key: 'status', label: i18n.t('status'), sortable: true },
    { key: 'user', label: i18n.t('user'), sortable: true },
    { key: 'from', label: i18n.t('from'), sortable: true, formatter: val => formatDate(val) },
    { key: 'to', label: i18n.t('to'), sortable: true, formatter: val => formatDate(val) },
    { key: 'total', label: i18n.t('total'), sortable: true, formatter: val => `${val}€` },
    { key: 'actions', label: i18n.t('actions') },
  ]
  const perPage = ref(10)
  const totalInvoices = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const statusFilter = ref({ label: i18n.t('pending'), value: 'pending' })
  let deleteId = null
  const totalPending = ref(0)
  let payrolls = ref(null)
  const payrollsSelected = ref([])
  const checkAll = ref(false)
  const isQuickShift = true

  const dataMeta = computed(() => {
    const localItemsCount = refInvoiceListTable.value ? refInvoiceListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInvoices.value,
    }
  })

  const refetchData = () => {
    refInvoiceListTable.value.refresh()
    refInvoiceListTable.value.clearSelected()
  }

  watch([currentPage, perPage, searchQuery, statusFilter], () => {
    refetchData()
  })

  const onPayrollsCreated = () => {
    toast({
      component: ToastificationContent,
      props: {
        title: i18n.t('success'),
        text: 'Palkkalaskelmat luotu',
        icon: 'CheckIcon',
        variant: 'success',
      },
    })
    refetchData()
  }

  const fetchPayrolls = (ctx, callback) => {
    fetchPayrollsSv({
      perPage: perPage.value,
      page: currentPage.value,
      status: statusFilter.value,
      isQuickShift,
    })
      .then(response => {
        payrolls = response.data
        totalPending.value = 0
        let index = 1
        payrolls.forEach(payroll => {
          /* eslint-disable no-param-reassign */
          payroll.index = index
          index += 1
          totalPending.value += parseFloat(payroll.total)
        })
        callback(payrolls)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe pikapalkkalaskelmien hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onDeleteInvoice = item => {
    if (item) {
      deleteId = item.id
    }
  }
  const confirmDelete = () => {
    deletePayroll({ id: deleteId })
      .then(() => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Palkkalaskelma poistettu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const exportCSV = () => {
    exportCSVList(payrollsSelected.value)
    payrollsSelected.value = []
    refInvoiceListTable.value.clearSelected()
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveInvoiceStatusVariantAndIcon = status => {
    if (status === 'pending') return { variant: 'warning', icon: 'PieChartIcon' }
    if (status === 'paid') return { variant: 'success', icon: 'CheckCircleIcon' }
    if (status === 'sent') return { variant: 'secondary', icon: 'SendIcon' }
    if (status === 'due') return { variant: 'danger', icon: 'InfoIcon' }
    return { variant: 'secondary', icon: 'XIcon' }
  }

  const resolveClientAvatarVariant = status => {
    if (status === 'Partial Payment') return 'primary'
    if (status === 'Paid') return 'danger'
    if (status === 'Downloaded') return 'secondary'
    if (status === 'Draft') return 'warning'
    if (status === 'Sent') return 'info'
    if (status === 'Past Due') return 'success'
    return 'primary'
  }

  const onRowSelected = items => {
    payrollsSelected.value = items
  }

  const tempContractIsActive = (startDate, endDate) => {
    const now = new Date().getTime()
    return startDate.toDate().getTime() < now && now < endDate.toDate().getTime()
  }

  const handleRowSelected = async (action, params) => {
    if (action === 'delete') {
      Promise.all(payrollsSelected.value.map(payroll => deletePayroll({ id: payroll.id })))
        .then(() => {
          refetchData()
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('success'),
              text: 'Palkkalaskelmat poistettu',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: i18n.t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    } else if (action === 'mark_paid') {
      Promise.all(payrollsSelected.value.map(payroll => markAsPaid({ id: payroll.id, status: params ? 'paid' : 'pending' })))
        .then(() => {
          refetchData()
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('success'),
              text: 'Tila päivitetty',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: i18n.t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    } else if (action === 'send') {
      try {
        await sendPayrolls(payrollsSelected.value)
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: i18n.t('success'),
            text: 'Palkkalaskelmat lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        refetchData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    } else if (action === 'download') {
      if (payrollsSelected.value.length > 1) {
        payrollsSelected.value.reduce(async (previous, payroll) => {
          try {
            if (previous) {
              if (typeof previous?.then === 'function') {
                await previous
              } else {
                await downloadPayroll(previous)
              }
            }
            if (payroll) { await downloadPayroll(payroll) }
          } catch (e) {
            toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: i18n.t('error'),
                text: i18n.t('something_went_wrong'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
      } else if (payrollsSelected.value.length === 1) {
        payrollsSelected.value.map(async payroll => {
          try {
            await downloadPayroll(payroll)
          } catch (e) {
            toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: i18n.t('error'),
                text: i18n.t('something_went_wrong'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
      }
    }
  }

  return {
    fetchPayrolls,
    tableColumns,
    perPage,
    currentPage,
    totalInvoices,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refInvoiceListTable,
    confirmDelete,
    statusFilter,

    resolveInvoiceStatusVariantAndIcon,
    resolveClientAvatarVariant,

    refetchData,
    onDeleteInvoice,
    onPayrollsCreated,
    totalPending,
    exportCSV,
    payrollsSelected,
    onRowSelected,
    handleRowSelected,
    checkAll,
    tempContractIsActive,
  }
}
